import { useQuery } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { Event } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface SearchFacets {
  [key: string]: {
    [value: string]: number
  }
}

/**
 * Algolia event structure from search results
 */
export interface AlgoliaEvent {
  object_id: string
  id: string
  name: string
  description?: string
  status: string
  color?: string
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  organizedBy?: string
  organizer?: {
    id: string
    firstName: string
    lastName: string
  }
  dates?: Array<{
    id: string
    startDate: string
    endDate: string
  }>
  location?: {
    id: string
    name?: string
    city?: string
    state?: string
  }
  totalRatingsCount?: number
  totalRatingsScore?: number
  keywords?: string[]
  gradeLevels?: string[]
  contentAreas?: string[]
  engagementOpportunities?: string[]
  connectionPreference?: string[]
  highlight_result?: {
    name?: {
      value: string
      match_level: string
      matched_words: string[]
      fully_highlighted?: boolean
    }
    description?: {
      value: string
      match_level: string
      matched_words: string[]
      fully_highlighted?: boolean
    }
    organizer?: {
      firstName?: {
        value: string
        matchLevel: string
        matchedWords: string[]
      }
      lastName?: {
        value: string
        matchLevel: string
        matchedWords: string[]
      }
    }
  }
}

/**
 * Response structure from the event search API
 */
export interface EventSearchResponse {
  items: AlgoliaEvent[]
  total: number
  page: number
  pages: number
  limit: number
  facets: SearchFacets
  facetsStats: Record<string, any> | null
  exhaustiveFacetsCount: boolean
  exhaustiveNbHits: boolean
  processingTimeMS: number
}

/**
 * Parameters for event search
 */
export interface EventSearchParams {
  query?: string
  facet_filters?: string[]
  filters?: string
  page?: number
  limit?: number
  adminSearch?: boolean

  // Additional filter parameters
  status?: string[]
  organizer?: string
  skip?: number
  includeInvited?: boolean
  includeCreated?: boolean
  includeOpen?: boolean

  // Legacy API parameters (for backward compatibility)
  keywords?: string[]
  content_areas?: string[]
  grade_levels?: string[]
}

/**
 * Hook for searching events with various filters and pagination
 */
export const useSearchEvents = (params: EventSearchParams = {}) => {
  const fetchWithAuth = useFetchWithAuth()

  // Set default values
  const {
    query = '',
    facet_filters,
    filters,
    page = 0,
    limit = 20,
    adminSearch,
    skip,
    status,
    organizer,
    includeInvited,
    includeCreated,
    includeOpen,
    keywords,
    content_areas,
    grade_levels,
  } = params

  return useQuery<EventSearchResponse>({
    queryKey: [
      'eventSearch',
      query,
      facet_filters,
      filters,
      page,
      limit,
      adminSearch,
      skip,
      status,
      organizer,
      includeInvited,
      includeCreated,
      includeOpen,
      keywords,
      content_areas,
      grade_levels,
    ],
    queryFn: async () => {
      // Build query params
      const queryParams = new URLSearchParams()

      if (query) queryParams.append('query', query)
      if (filters) queryParams.append('filters', filters)
      if (page !== undefined) queryParams.append('page', page.toString())
      if (limit !== undefined) queryParams.append('limit', limit.toString())
      if (adminSearch) queryParams.append('admin_search', 'true')
      if (skip !== undefined) queryParams.append('skip', skip.toString())
      if (organizer) queryParams.append('organizer', organizer)

      // Include flags
      if (includeInvited !== undefined)
        queryParams.append('include_invited', includeInvited.toString())
      if (includeCreated !== undefined)
        queryParams.append('include_created', includeCreated.toString())
      if (includeOpen !== undefined)
        queryParams.append('include_open', includeOpen.toString())

      // Add facet filters if present
      if (Array.isArray(facet_filters) && facet_filters.length > 0) {
        facet_filters.forEach((filter) => {
          queryParams.append('facet_filters', filter)
        })
      }

      // Add status filters if present
      if (Array.isArray(status) && status.length > 0) {
        status.forEach((s) => {
          queryParams.append('status', s)
        })
      }

      // Add keyword filters if present
      if (Array.isArray(keywords) && keywords.length > 0) {
        keywords.forEach((keyword) => {
          queryParams.append('keywords', keyword)
        })
      }

      // Add content area filters if present
      if (Array.isArray(content_areas) && content_areas.length > 0) {
        content_areas.forEach((area) => {
          queryParams.append('content_areas', area)
        })
      }

      // Add grade level filters if present
      if (Array.isArray(grade_levels) && grade_levels.length > 0) {
        grade_levels.forEach((level) => {
          queryParams.append('grade_levels', level)
        })
      }

      // Construct the request URL for the search endpoint
      const requestUrl = `${API_URL}/events/search?${queryParams.toString()}`

      // Log the full request URL for debugging
      console.log('Event search request URL:', requestUrl)

      // Fetch events from API with filters
      const response = await fetchWithAuth(requestUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      // Log response for debugging
      console.log('Event search API response:', response)

      // Ensure response has correct structure, providing fallbacks for missing data
      const safeResponse: EventSearchResponse = {
        items: Array.isArray(response?.items) ? response.items : [],
        total: response?.total || 0,
        page: response?.page || 0,
        pages: response?.pages || 0,
        limit: response?.limit || 0,
        facets: response?.facets || {},
        facetsStats: response?.facetsStats || null,
        exhaustiveFacetsCount: response?.exhaustiveFacetsCount || false,
        exhaustiveNbHits: response?.exhaustiveNbHits || false,
        processingTimeMS: response?.processingTimeMS || 0,
      }

      return safeResponse
    },
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

/**
 * Hook for simple keyword search of events
 * A convenience wrapper around useSearchEvents for simple use cases
 */
export const useKeywordEventSearch = (keyword: string, limit: number = 20) => {
  return useSearchEvents({
    query: keyword,
    limit,
  })
}

/**
 * Hook for finding events by status
 * A convenience wrapper around useSearchEvents for common status-based queries
 */
export const useStatusEventSearch = (
  status: string | string[],
  limit: number = 12
) => {
  const statusArray = Array.isArray(status) ? status : [status]

  return useSearchEvents({
    status: statusArray,
    limit,
  })
}

/**
 * Helper function to convert an AlgoliaEvent to the standard Event type
 * This can be useful when working with components that expect the old Event format
 */
export const convertToEvent = (algoliaEvent: AlgoliaEvent): Event => {
  return {
    id: algoliaEvent.id || algoliaEvent.object_id,
    name: algoliaEvent.name,
    description: algoliaEvent.description,
    status: algoliaEvent.status,
    created_at: algoliaEvent.createdAt,
    updated_at: algoliaEvent.updatedAt,
    created_by: algoliaEvent.createdBy,
    organized_by: algoliaEvent.organizedBy,
    color: algoliaEvent.color,
    keywords: algoliaEvent.keywords,
    grade_levels: algoliaEvent.gradeLevels,
    content_areas: algoliaEvent.contentAreas,
    engagement_opportunities: algoliaEvent.engagementOpportunities,
    dates: algoliaEvent.dates,
    location: algoliaEvent.location,
    total_ratings_count: algoliaEvent.totalRatingsCount,
    total_ratings_score: algoliaEvent.totalRatingsScore,
    organizer: algoliaEvent.organizer
      ? {
          id: algoliaEvent.organizer.id,
          firstName: algoliaEvent.organizer.firstName,
          lastName: algoliaEvent.organizer.lastName,
        }
      : undefined,
  } as Event
}
